import React from 'react';
import { Shield, Clock, Lock, AlertTriangle } from 'lucide-react';
import { SecurityConfig } from '../PlatformSettings';

interface SecuritySettingsFormProps {
  settings: SecurityConfig;
  onUpdate: (updates: Partial<SecurityConfig>) => void;
}

export const SecuritySettingsForm: React.FC<SecuritySettingsFormProps> = ({ settings, onUpdate }) => {
  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      let updates: any = {};
      
      if (parts.length === 2) {
        const [parent, child] = parts;
        updates = {
          [parent]: {
            ...settings[parent as keyof SecurityConfig],
            [child]: value,
          },
        };
      }
      
      onUpdate(updates);
    } else {
      onUpdate({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Shield className="w-6 h-6 text-blue-500 mr-3" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Security Settings</h3>
          <p className="text-sm text-gray-600">Configure platform security policies and restrictions</p>
        </div>
      </div>

      {/* Session Management */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center">
          <Clock className="w-4 h-4 mr-2" />
          Session Management
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Session Timeout (minutes)
            </label>
            <input
              type="number"
              min="5"
              max="1440"
              value={settings.sessionTimeout}
              onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value) || 480)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">5 minutes to 24 hours</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Login Attempts
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={settings.maxLoginAttempts}
              onChange={(e) => handleInputChange('maxLoginAttempts', parseInt(e.target.value) || 5)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Before account lockout</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lockout Duration (minutes)
            </label>
            <input
              type="number"
              min="1"
              max="1440"
              value={settings.lockoutDuration}
              onChange={(e) => handleInputChange('lockoutDuration', parseInt(e.target.value) || 30)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Account lockout time</p>
          </div>
        </div>

        {/* Advanced Session Settings */}
        <div className="mt-6 space-y-4">
          <h5 className="font-medium text-gray-900">Advanced Session Settings</h5>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Warning (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.sessionWarningMinutes || 15}
                onChange={(e) => handleInputChange('sessionWarningMinutes', parseInt(e.target.value) || 15)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Warn before timeout</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remember Me Duration (days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.rememberMeDuration || 30}
                onChange={(e) => handleInputChange('rememberMeDuration', parseInt(e.target.value) || 30)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">1 day to 1 year</p>
            </div>
          </div>

          {/* Session Options */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="rememberMeEnabled"
                type="checkbox"
                checked={settings.rememberMeEnabled || false}
                onChange={(e) => handleInputChange('rememberMeEnabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rememberMeEnabled" className="ml-2 block text-sm text-gray-700">
                Enable "Remember Me" option on login
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="activityBasedExtension"
                type="checkbox"
                checked={settings.activityBasedExtension || false}
                onChange={(e) => handleInputChange('activityBasedExtension', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="activityBasedExtension" className="ml-2 block text-sm text-gray-700">
                Extend session automatically based on user activity
              </label>
            </div>
          </div>

          {/* Role-based Timeouts */}
          <div className="mt-6">
            <h5 className="font-medium text-gray-900 mb-3">Role-based Session Timeouts</h5>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Super Admin (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={settings.roleBasedTimeouts?.SuperAdmin || 240}
                  onChange={(e) => handleInputChange('roleBasedTimeouts.SuperAdmin', parseInt(e.target.value) || 240)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Firm Admin (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={settings.roleBasedTimeouts?.FirmAdmin || 360}
                  onChange={(e) => handleInputChange('roleBasedTimeouts.FirmAdmin', parseInt(e.target.value) || 360)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manager (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={settings.roleBasedTimeouts?.Manager || 480}
                  onChange={(e) => handleInputChange('roleBasedTimeouts.Manager', parseInt(e.target.value) || 480)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Staff (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={settings.roleBasedTimeouts?.Staff || 480}
                  onChange={(e) => handleInputChange('roleBasedTimeouts.Staff', parseInt(e.target.value) || 480)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client (min)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={settings.roleBasedTimeouts?.Client || 720}
                  onChange={(e) => handleInputChange('roleBasedTimeouts.Client', parseInt(e.target.value) || 720)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Policy */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4 flex items-center">
          <Lock className="w-4 h-4 mr-2" />
          Password Policy
        </h4>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Length
              </label>
              <input
                type="number"
                min="6"
                max="128"
                value={settings.passwordPolicy.minLength}
                onChange={(e) => handleInputChange('passwordPolicy.minLength', parseInt(e.target.value) || 8)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password Max Age (days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.passwordPolicy.maxAge}
                onChange={(e) => handleInputChange('passwordPolicy.maxAge', parseInt(e.target.value) || 90)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="require-uppercase"
                checked={settings.passwordPolicy.requireUppercase}
                onChange={(e) => handleInputChange('passwordPolicy.requireUppercase', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="require-uppercase" className="ml-2 block text-sm text-gray-900">
                Require Uppercase
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="require-lowercase"
                checked={settings.passwordPolicy.requireLowercase}
                onChange={(e) => handleInputChange('passwordPolicy.requireLowercase', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="require-lowercase" className="ml-2 block text-sm text-gray-900">
                Require Lowercase
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="require-numbers"
                checked={settings.passwordPolicy.requireNumbers}
                onChange={(e) => handleInputChange('passwordPolicy.requireNumbers', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="require-numbers" className="ml-2 block text-sm text-gray-900">
                Require Numbers
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="require-symbols"
                checked={settings.passwordPolicy.requireSymbols}
                onChange={(e) => handleInputChange('passwordPolicy.requireSymbols', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="require-symbols" className="ml-2 block text-sm text-gray-900">
                Require Symbols
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Factor Authentication */}
      <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
        <h4 className="font-medium text-gray-900 mb-4">Multi-Factor Authentication</h4>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="mfa-required"
            checked={settings.mfaRequired}
            onChange={(e) => handleInputChange('mfaRequired', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="mfa-required" className="ml-2 block text-sm text-gray-900">
            Require MFA for all users
          </label>
        </div>
        
        {settings.mfaRequired && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="flex items-center">
              <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
              <span className="text-sm text-yellow-800">
                Enabling MFA will require all users to set up two-factor authentication on their next login.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* IP Restrictions */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-4">IP Address Restrictions</h4>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Allowed IP Addresses (One per line)
          </label>
          <textarea
            value={settings.allowedIPs.join('\n')}
            onChange={(e) => handleInputChange('allowedIPs', e.target.value.split('\n').filter(ip => ip.trim()))}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="192.168.1.0/24&#10;10.0.0.0/8&#10;203.0.113.1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to allow access from any IP. Supports CIDR notation (e.g., 192.168.1.0/24)
          </p>
        </div>
      </div>

      {/* Security Recommendations */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h5 className="font-medium text-green-900 mb-2">Security Recommendations:</h5>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• Set session timeout to 8 hours or less for better security</li>
          <li>• Enable MFA for all administrative accounts</li>
          <li>• Use strong password policies with mixed case, numbers, and symbols</li>
          <li>• Limit login attempts to prevent brute force attacks</li>
          <li>• Consider IP restrictions for highly sensitive environments</li>
          <li>• Regularly review and update security settings</li>
        </ul>
      </div>

      {/* Current Security Status */}
      <div className="border rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-4">Current Security Status</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold text-blue-600">{settings.sessionTimeout}</div>
            <div className="text-sm text-gray-600">Session Timeout (min)</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold text-blue-600">{settings.maxLoginAttempts}</div>
            <div className="text-sm text-gray-600">Max Login Attempts</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold text-blue-600">{settings.passwordPolicy.minLength}</div>
            <div className="text-sm text-gray-600">Min Password Length</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded">
            <div className={`text-2xl font-bold ${settings.mfaRequired ? 'text-green-600' : 'text-red-600'}`}>
              {settings.mfaRequired ? 'ON' : 'OFF'}
            </div>
            <div className="text-sm text-gray-600">MFA Required</div>
          </div>
        </div>
      </div>
    </div>
  );
};
